/* ============================================================
   NuveRise — Analytics & Tracking Event Infrastructure
   ------------------------------------------------------------
   Single source of truth for funnel events. Fires today into
   four ambient queues so that the second the merchant installs
   Klaviyo / Postscript / GA4-GTM / Meta Pixel, full-funnel
   tracking activates without any theme changes:

     1.  window.dataLayer     — GTM / GA4 standard
     2.  window._learnq       — Klaviyo standard (auto-flushes
                                 once klaviyo.js loads)
     3.  window.Postscript    — Postscript pixel (if installed)
     4.  CustomEvent          — `nuve:track` for any in-theme
                                 listener that wants to react.

   Public API:
     NuveTrack.track(eventName, properties)
     NuveTrack.identify(email, traits)

   Debug:  localStorage.setItem('nuve_analytics_debug', '1')
   ============================================================ */
(function () {
  'use strict';

  // ----- Ambient queue bootstrap -----
  window.dataLayer = window.dataLayer || [];
  window._learnq = window._learnq || [];

  // ----- Debug logger -----
  var DEBUG = false;
  try {
    DEBUG = window.localStorage.getItem('nuve_analytics_debug') === '1';
  } catch (e) { /* localStorage blocked */ }
  var log = DEBUG ? console.log.bind(console, '[NuveTrack]') : function () {};

  // ----- Helpers -----
  function toNumber(v) {
    if (v == null) return null;
    var n = parseFloat(String(v).replace(/[^0-9.\-]/g, ''));
    return isFinite(n) ? n : null;
  }

  function toInt(v) {
    var n = parseInt(v, 10);
    return isNaN(n) ? 0 : n;
  }

  function klaviyoEventName(eventName) {
    // Klaviyo prefers Title Case readable event names
    return String(eventName)
      .replace(/_/g, ' ')
      .replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }

  // ----- Core track -----
  function track(eventName, props) {
    props = props || {};

    var payload = Object.assign({
      event: eventName,
      timestamp: Date.now(),
      page_path: window.location.pathname,
      page_title: document.title
    }, props);

    // 1. GTM / GA4 dataLayer
    try { window.dataLayer.push(payload); } catch (e) {}

    // 2. Klaviyo — name + props separate. Queues until klaviyo.js loads.
    try { window._learnq.push(['track', klaviyoEventName(eventName), props]); } catch (e) {}

    // 3. Postscript pixel (if installed)
    try {
      if (window.Postscript && typeof window.Postscript.track === 'function') {
        window.Postscript.track(eventName, props);
      }
    } catch (e) {}

    // 4. In-theme listeners
    try {
      document.dispatchEvent(new CustomEvent('nuve:track', {
        detail: { eventName: eventName, props: props }
      }));
    } catch (e) {}

    log(eventName, props);
  }

  function identify(email, traits) {
    traits = traits || {};
    try {
      window.dataLayer.push(Object.assign({ event: 'identify', user_email: email }, traits));
    } catch (e) {}
    try {
      var kProps = Object.assign({ '$email': email }, traits);
      window._learnq.push(['identify', kProps]);
    } catch (e) {}
    try {
      if (window.Postscript && typeof window.Postscript.identify === 'function') {
        window.Postscript.identify(email, traits);
      }
    } catch (e) {}
    log('identify', email, traits);
  }

  // ============================================================
  // AUTO-FIRE ON LOAD
  // ============================================================
  function autoFire() {
    var meta = document.querySelector('[data-nuve-page]');
    var tpl = meta && meta.dataset ? (meta.dataset.template || '') : '';

    // 1. page_view — always
    track('page_view', {
      template: tpl,
      referrer: document.referrer || ''
    });

    // 2. product_view
    if (tpl.indexOf('product') !== -1) {
      var p = document.querySelector('[data-current-product]') || meta;
      if (p && p.dataset && p.dataset.productId) {
        var goals = [];
        if (p.dataset.goals) {
          goals = p.dataset.goals.split(',').map(function (g) { return g.trim(); }).filter(Boolean);
        }
        track('product_view', {
          product_id: p.dataset.productId,
          handle: p.dataset.handle,
          title: p.dataset.title,
          price: toNumber(p.dataset.price),
          compare_at: toNumber(p.dataset.compareAt),
          goals: goals,
          vendor: p.dataset.vendor,
          available: p.dataset.available !== 'false'
        });
      }
    }

    // 3. collection_view
    if (tpl.indexOf('collection') !== -1 && meta) {
      track('collection_view', {
        collection_handle: meta.dataset.collectionHandle,
        collection_title: meta.dataset.collectionTitle,
        product_count: toInt(meta.dataset.productCount)
      });
    }

    // 4. cart_view (cart page only — drawer-open fires its own event)
    if (tpl === 'cart' || window.location.pathname === '/cart') {
      track('cart_view', {
        total_value: toNumber(meta && meta.dataset.cartTotal) || 0,
        item_count: toInt(meta && meta.dataset.cartCount)
      });
    }

    // 5. search — Shopify's search results page
    if (tpl.indexOf('search') !== -1) {
      var qParam = new URLSearchParams(window.location.search).get('q') || '';
      var resultCount = toInt(meta && meta.dataset.searchResultCount);
      // result count attr is optional — if missing, count rendered cards as fallback
      if (!resultCount) {
        resultCount = document.querySelectorAll('[data-product-card], [data-search-result]').length;
      }
      if (qParam) {
        track('search', { query: qParam, result_count: resultCount });
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoFire);
  } else {
    autoFire();
  }

  // ============================================================
  // DOM EVENT BRIDGE
  // Other theme modules dispatch CustomEvents which we forward.
  // These events may not exist today — they activate as modules wire.
  // ============================================================

  // Cart
  document.addEventListener('cart:add', function (e) {
    track('add_to_cart', e.detail || {});
  });
  document.addEventListener('cart:remove', function (e) {
    track('remove_from_cart', e.detail || {});
  });
  document.addEventListener('cart:open', function (e) {
    var d = e.detail || {};
    track('cart_view', {
      total_value: toNumber(d.total_value),
      item_count: toInt(d.item_count)
    });
  });

  // Wishlist — detail can be either { added: {...} } / { removed: {...} } or a raw array
  document.addEventListener('wishlist:change', function (e) {
    var d = e.detail || {};
    if (d.added) {
      track('wishlist_add', {
        product_id: d.added.product_id || d.added.id,
        handle: d.added.handle,
        title: d.added.title
      });
    } else if (d.removed) {
      track('wishlist_remove', {
        product_id: d.removed.product_id || d.removed.id,
        handle: d.removed.handle
      });
    } else if (d._removed) {
      track('wishlist_remove', {
        product_id: d._removed.product_id || d._removed.id,
        handle: d._removed.handle
      });
    } else if (Array.isArray(d) && d[0]) {
      track('wishlist_add', {
        product_id: d[0].product_id || d[0].id,
        handle: d[0].handle,
        title: d[0].title
      });
    }
  });

  // Concierge quiz
  document.addEventListener('quiz:start', function () { track('quiz_start'); });
  document.addEventListener('quiz:step', function (e) { track('quiz_step', e.detail || {}); });
  document.addEventListener('quiz:complete', function (e) {
    var d = e.detail || {};
    track('quiz_complete', d);
    // If email captured at quiz end, identify the visitor
    if (d.email) identify(d.email, { source: 'concierge' });
  });
  document.addEventListener('concierge:recommend-add', function (e) {
    track('concierge_recommend_add', e.detail || {});
  });

  // Newsletter / welcome popup
  document.addEventListener('newsletter:subscribe', function (e) {
    var d = e.detail || {};
    track('newsletter_subscribe', d);
    if (d.email) identify(d.email, { source: d.source || 'newsletter' });
  });
  document.addEventListener('welcome:show', function (e) {
    track('welcome_popup_show', e.detail || {});
  });
  document.addEventListener('welcome:dismiss', function () {
    track('welcome_popup_dismiss');
  });
  document.addEventListener('welcome:subscribe', function (e) {
    var d = e.detail || {};
    track('welcome_popup_subscribe', d);
    if (d.email) identify(d.email, { source: 'welcome-popup' });
  });

  // Subscribe & Save toggle on PDP
  document.addEventListener('subscribe-save:toggle', function (e) {
    track('subscribe_save_toggle', e.detail || {});
  });

  // ============================================================
  // PREDICTIVE SEARCH — debounced
  // ============================================================
  var searchDebounce;
  document.addEventListener('input', function (e) {
    var t = e.target;
    if (!t || !t.matches) return;
    if (!t.matches('[data-psearch-input], input[name="q"]')) return;
    clearTimeout(searchDebounce);
    var q = t.value || '';
    searchDebounce = setTimeout(function () {
      if (q.length >= 2) track('predictive_search', { query: q });
    }, 600);
  });

  // ============================================================
  // CLICK BRIDGE
  // - checkout buttons → checkout_started
  // - sticky ATC bar → sticky_atc_click
  // ============================================================
  document.addEventListener('click', function (e) {
    var target = e.target;
    if (!target || !target.closest) return;

    // Checkout buttons (form submit, /checkout link, button[name=checkout])
    var checkoutBtn = target.closest(
      'button[name="checkout"], input[name="checkout"], a[href="/checkout"], a[href^="/checkout?"], button[type="submit"][formaction*="/checkout"]'
    );
    if (checkoutBtn) {
      var totalEl = document.querySelector('[data-cart-total]');
      var countEl = document.querySelector('[data-cart-count]');
      track('checkout_started', {
        total_value: toNumber(totalEl && totalEl.textContent),
        item_count: toInt(countEl && countEl.textContent)
      });
    }

    // Sticky ATC bar
    var stickyAtc = target.closest('.sticky-atc button[type="submit"], [data-sticky-atc-button], [data-sticky-atc] button');
    if (stickyAtc) {
      var cp = document.querySelector('[data-current-product]') || document.querySelector('[data-nuve-page]');
      track('sticky_atc_click', {
        product_id: cp && cp.dataset ? cp.dataset.productId : null,
        handle: cp && cp.dataset ? cp.dataset.handle : null
      });
    }
  });

  // ============================================================
  // FORM POSTED — Shopify customer.posted / contact / newsletter
  // Detects ?customer_posted=true or ?form_type fragments and fires
  // newsletter_subscribe if the form was a customer signup.
  // (Backup path — direct dispatch from form modules is preferred.)
  // ============================================================
  try {
    var url = new URL(window.location.href);
    if (url.searchParams.get('customer_posted') === 'true') {
      var emailField = document.querySelector('input[name="contact[email]"], input[type="email"]');
      var email = emailField && emailField.value ? emailField.value : null;
      track('newsletter_subscribe', {
        source: document.querySelector('[data-newsletter-source]')
          ? document.querySelector('[data-newsletter-source]').dataset.newsletterSource
          : 'footer',
        email: email
      });
      if (email) identify(email, { source: 'newsletter' });
    }
  } catch (e) {}

  // ============================================================
  // PUBLIC API
  // ============================================================
  window.NuveTrack = {
    track: track,
    identify: identify,
    _version: '1.0.0'
  };

  log('NuveTrack ready');
})();
